import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Project credentials vault (mirrors backend/src/routes/projects.vault.ts).
//
// Secret values NEVER arrive through the list endpoint: they come back as
// `value: null` with `hasValue: true`, and only `revealVaultEntry` returns the
// plaintext — which the server records in the audit log every single time.
// ---------------------------------------------------------------------------

export const VAULT_TYPES = [
  'SERVER',
  'GITHUB',
  'GITLAB',
  'AZURE',
  'AWS',
  'DATABASE',
  'EMAIL',
  'FTP',
  'API',
  'HOSTING',
  'DOMAIN',
  'ACCOUNT',
  'OTHER',
] as const;

export type VaultType = (typeof VAULT_TYPES)[number];

export interface VaultFieldInput {
  label: string;
  value: string;
  secret: boolean;
}

export interface VaultField {
  label: string;
  secret: boolean;
  /** Null for secret fields until they are revealed. */
  value: string | null;
  hasValue: boolean;
}

export interface VaultEntry {
  id: string;
  title: string;
  type: string;
  url: string | null;
  notes: string | null;
  fields: VaultField[];
  /** The stored blob could not be decrypted (master secret rotated?). */
  decryptError: boolean;
  createdBy: { id: string; name: string } | null;
  updatedBy: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface VaultEntryInput {
  title: string;
  type: VaultType;
  url: string | null;
  notes: string | null;
  fields: VaultFieldInput[];
}

export interface VaultAuditEntry {
  id: string;
  entryId: string | null;
  entryTitle: string;
  action: string;
  userId: string | null;
  userName: string;
  details: string | null;
  createdAt: string;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

/** GET /api/projects/:id/vault — all entries, secret values masked. */
export async function listVaultEntries(projectId: string): Promise<VaultEntry[]> {
  const res = await apiFetch<Envelope<VaultEntry[]>>(`/api/projects/${projectId}/vault`, {
    headers: authHeaders(),
  });
  return res.data;
}

/** POST /api/projects/:id/vault — create (projects.canEditVault). */
export async function createVaultEntry(
  projectId: string,
  input: VaultEntryInput,
): Promise<VaultEntry> {
  const res = await apiFetch<Envelope<VaultEntry>>(`/api/projects/${projectId}/vault`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/**
 * PUT /api/projects/:id/vault/:entryId — replace (projects.canEditVault).
 * A secret field sent with an empty value keeps whatever is already stored.
 */
export async function updateVaultEntry(
  projectId: string,
  entryId: string,
  input: VaultEntryInput,
): Promise<VaultEntry> {
  const res = await apiFetch<Envelope<VaultEntry>>(`/api/projects/${projectId}/vault/${entryId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/** DELETE /api/projects/:id/vault/:entryId (projects.canEditVault). */
export async function deleteVaultEntry(projectId: string, entryId: string): Promise<void> {
  await apiFetch<Envelope<{ id: string; deleted: boolean }>>(
    `/api/projects/${projectId}/vault/${entryId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

/**
 * POST /api/projects/:id/vault/:entryId/reveal — plaintext secrets. Requires
 * canViewVault AND canRevealVault, and writes a REVEAL audit row every call.
 * Omit `fieldLabel` to reveal every secret field of the entry.
 */
export async function revealVaultEntry(
  projectId: string,
  entryId: string,
  fieldLabel?: string,
): Promise<{ label: string; value: string }[]> {
  const res = await apiFetch<Envelope<{ id: string; fields: { label: string; value: string }[] }>>(
    `/api/projects/${projectId}/vault/${entryId}/reveal`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(fieldLabel ? { fieldLabel } : {}),
    },
  );
  return res.data.fields;
}

/** GET /api/projects/:id/vault/audit — access log (projects.canEditVault). */
export async function listVaultAudit(projectId: string): Promise<VaultAuditEntry[]> {
  const res = await apiFetch<Envelope<VaultAuditEntry[]>>(
    `/api/projects/${projectId}/vault/audit`,
    { headers: authHeaders() },
  );
  return res.data;
}
